import React, { useEffect, useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import { PictureAsPdf, RemoveRedEye } from "@mui/icons-material";
import { fetchVendorAppointmentDetails, fetchVendorAppointmentList } from "Service/ApiService";
import { useLoading } from "context/LoadingContext";
import { useAlert } from "context/AlertContext";

const EWAY_BILL_BASE_URL = "https://dev.siyaram.com/nirman/api/vendor/uploads/asn_EwayBill/";

const formatDateValue = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AppointmentListController = () => {
  const [appointmentRows, setAppointmentRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoadingState] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [openDetailsPopup, setOpenDetailsPopup] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentDetailRows, setAppointmentDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const { setLoading } = useLoading();
  const { showAlert } = useAlert();
  const vendorDetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendorDetails?.vendorCode || null;

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!vendorCode) {
        setAppointmentRows([]);
        setRowCount(0);
        return;
      }

      try {
        setLoading(true);
        setLoadingState(true);
        const response = await fetchVendorAppointmentList(
          vendorCode,
          paginationModel.page + 1,
          paginationModel.pageSize
        );

        if (response?.success === "true") {
          const rows = (response.asnAppointmentList || []).map((item, index) => ({
            id: item.asnAppointmentId,
            srNo: paginationModel.page * paginationModel.pageSize + index + 1,
            asnAppointmentId: item.asnAppointmentId,
            vendorCode: item.vendor_code || "-",
            vendorName: item.name || "-",
            vendorPoId: item.vendorPoId || "-",
            poNumber: item.poNumber || "-",
            appointmentDate: item.appointmentDate || "-",
            appointmentDateLabel: formatDateValue(item.appointmentDate),
            vendorDispatchQuantity: item.vendor_dispatch_quantity || "0.00",
            ewayBillPdf: item.ewayBillPdf || "",
            isGateEntrySubmit: item.isGateEntrySubmit || "-",
          }));

          setAppointmentRows(rows);
          setRowCount(Number(response.pagination?.totalRecords || rows.length || 0));
        } else {
          setAppointmentRows([]);
          setRowCount(0);
        }
      } catch (error) {
        setAppointmentRows([]);
        setRowCount(0);
        showAlert("Unable to fetch appointment list.", {
          icon: "error",
          title: "Error",
        });
      } finally {
        setLoading(false);
        setLoadingState(false);
      }
    };

    fetchAppointments();
  }, [paginationModel, setLoading, showAlert, vendorCode]);

  const handleViewDetails = async (row) => {
    try {
      setSelectedAppointment(row);
      setAppointmentDetailRows([]);
      setOpenDetailsPopup(true);
      setDetailLoading(true);

      const response = await fetchVendorAppointmentDetails(row.asnAppointmentId);

      if (response?.success === "true") {
        const rows = (response.asnAppointmentDetailsList || []).map((item) => ({
          id: item.asnAppointmentDtlsId,
          materialCode: item.materialCode || "-",
          boxNumber: item.boxNumber || "-",
          brand: item.brand || "-",
          product: item.product || "-",
          materialDesc: item.materialDesc || "-",
          barcode: item.barcode || "-",
          quantity: item.quantity || "0.00",
        }));

        setAppointmentDetailRows(rows);
      } else {
        setAppointmentDetailRows([]);
        showAlert(response?.msg || "No appointment details found.", {
          icon: "info",
          title: "No Data",
        });
      }
    } catch (error) {
      setAppointmentDetailRows([]);
      showAlert("Unable to fetch appointment details.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailsPopup = () => {
    setOpenDetailsPopup(false);
    setSelectedAppointment(null);
    setAppointmentDetailRows([]);
    setDetailLoading(false);
  };

  const handleOpenEwayBill = (fileName) => {
    if (!fileName || !String(fileName).trim()) {
      return;
    }

    const fileUrl = `${EWAY_BILL_BASE_URL}${encodeURIComponent(fileName)}`;
    const link = document.createElement("a");

    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: "srNo", headerName: "Sr No", flex: 0.7, minWidth: 90 },
    { field: "poNumber", headerName: "PO Number", flex: 1, minWidth: 140 },
    // { field: "vendorCode", headerName: "Vendor Code", flex: 1, minWidth: 130 },
    // { field: "vendorName", headerName: "Vendor Name", flex: 1.4, minWidth: 180 },
    { field: "appointmentDateLabel", headerName: "Appointment Date", flex: 1, minWidth: 150 },
    {
      field: "vendorDispatchQuantity",
      headerName: "Dispatch Qty",
      flex: 0.9,
      minWidth: 130,
    },
    { field: "isGateEntrySubmit", headerName: "Gate Entry", flex: 0.9, minWidth: 120 },
    {
      field: "ewayBillPdf",
      headerName: "E-way Bill PDF",
      minWidth: 130,
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasEwayBill = Boolean(params.row.ewayBillPdf && params.row.ewayBillPdf.trim());

        return (
          <Button
            disabled={!hasEwayBill}
            onClick={(event) => {
              event.stopPropagation();
              if (hasEwayBill) {
                handleOpenEwayBill(params.row.ewayBillPdf);
              }
            }}
            startIcon={<PictureAsPdf sx={{ fontSize: "2rem !important" }} />}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton color="error" size="small" onClick={() => handleViewDetails(params.row)}>
            <RemoveRedEye fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const detailColumns = [
    { field: "materialCode", headerName: "Material Code", flex: 1.2, minWidth: 160 },
    { field: "boxNumber", headerName: "Box Number", flex: 0.8, minWidth: 110 },
    { field: "brand", headerName: "Brand", flex: 1, minWidth: 120 },
    { field: "product", headerName: "Product", flex: 1, minWidth: 120 },
    { field: "materialDesc", headerName: "Material Desc", flex: 1.6, minWidth: 220 },
    { field: "barcode", headerName: "Barcode", flex: 1.2, minWidth: 170 },
    { field: "quantity", headerName: "Quantity", flex: 0.8, minWidth: 100 },
  ];

  return {
    loading,
    appointmentRows,
    rowCount,
    paginationModel,
    setPaginationModel,
    columns,
    openDetailsPopup,
    handleCloseDetailsPopup,
    selectedAppointment,
    appointmentDetailRows,
    detailColumns,
    detailLoading,
  };
};

export default AppointmentListController;
